import { type FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useProductDetail } from '../../hooks/useProductDetail';
import ProductForm from './components/ProductForm';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(price / 100);

const formatDateTime = (value: string | null) =>
    value ? new Date(value).toLocaleString() : '—';

// ── Component ─────────────────────────────────────────────────────────────────

const PriceHistoryPage: FC = () => {
  const { productId, storeId } = useParams<{ productId: string; storeId: string }>();
  const { product, prices, loading, fetchProductDetail } = useProductDetail();
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    if (productId) void fetchProductDetail(productId, storeId);
  }, [productId, storeId, fetchProductDetail]);

  const currentPrice = product?.display_price ?? prices[0]?.price ?? 0;

  return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-stone-900">Product Details</h2>
          <p className="mt-1 text-sm text-stone-500">
            View product information, status, pricing, and price changes in one place.
          </p>
        </div>

        {/* Product card */}
        <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
          <div className="h-1 bg-[#ee2b74]" />
          {loading ? (
              <div className="p-6 animate-pulse">
                <div className="h-6 w-1/3 rounded bg-stone-100 mb-3" />
                <div className="h-4 w-1/2 rounded bg-stone-100 mb-6" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="h-20 rounded-xl bg-stone-100" />
                  ))}
                </div>
              </div>
          ) : (
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                  <div className="w-full lg:w-32 h-32 rounded-2xl border border-stone-200 bg-stone-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {product?.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-xs font-medium text-stone-400">No image</span>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-xl font-semibold text-stone-900">
                            {product?.name ?? 'Unknown Product'}
                          </h3>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                              product?.is_active
                                  ? 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]'
                                  : 'bg-stone-100 text-stone-600 ring-stone-200'
                          }`}>
                                                {product?.is_active ? 'Active' : 'Inactive'}
                                            </span>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                              product?.health
                                  ? 'bg-blue-50 text-blue-700 ring-blue-200'
                                  : 'bg-stone-100 text-stone-600 ring-stone-200'
                          }`}>
                                                {product?.health ? 'Health Product' : 'Standard Product'}
                                            </span>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2">
                                            <span className="inline-flex rounded-md bg-stone-100 px-2 py-1 font-mono text-xs text-stone-600">
                                                SKU: {product?.sku ?? '—'}
                                            </span>
                          <span className="inline-flex rounded-md bg-stone-100 px-2 py-1 text-xs text-stone-600">
                                                Store: {product?.store_name ?? 'No Store'}
                                            </span>
                          <span className="inline-flex rounded-md bg-stone-100 px-2 py-1 text-xs text-stone-600">
                                                Stock: {product?.stock ?? 0}
                                            </span>
                        </div>

                        {product?.keywords && product.keywords.length > 0 && (
                            <div className="mt-4">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Keywords</p>
                              <div className="flex flex-wrap gap-2">
                                {product.keywords.map(kw => (
                                    <span key={kw} className="inline-flex items-center rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700">
                                                            {kw}
                                                        </span>
                                ))}
                              </div>
                            </div>
                        )}

                        {product?.description && (
                            <div className="mt-4">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-2">Description</p>
                              <p className="max-w-3xl text-sm text-stone-600 leading-6">{product.description}</p>
                            </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-3">
                        <div className="rounded-2xl bg-stone-50 border border-stone-200 px-4 py-3 min-w-[180px]">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Current Price</p>
                          <p className="mt-1 text-2xl font-semibold text-stone-900">{formatPrice(currentPrice)}</p>
                          <p className="mt-1 text-xs text-stone-500">Latest available price</p>
                        </div>
                        <button type="button" onClick={() => setShowEditForm(true)}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors">
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit Product
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          )}
        </div>

        {/* Price history */}
        <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/60">
            <h4 className="text-sm font-semibold text-stone-900">Price Changes</h4>
            <p className="text-xs text-stone-500 mt-1">Historical pricing entries for this product.</p>
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white border-b border-stone-200">
              <tr>
                {['Price', 'Valid From', 'Valid To', 'Status'].map(h => (
                    <th key={h} className="py-3 px-6 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                ))}
              </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
              {loading ? (
                  <tr><td colSpan={4} className="py-10 px-6 text-stone-400">Loading…</td></tr>
              ) : prices.length === 0 ? (
                  <tr><td colSpan={4} className="py-10 px-6 text-stone-400">No history found</td></tr>
              ) : prices.map((r, i) => {
                const isCurrent = !r.valid_to;
                return (
                    <tr key={i} className="hover:bg-stone-50/50 transition-colors">
                      <td className="py-4 px-6 font-semibold text-stone-900">{formatPrice(r.price)}</td>
                      <td className="py-4 px-6 text-stone-600">{formatDateTime(r.valid_from)}</td>
                      <td className="py-4 px-6 text-stone-600">{formatDateTime(r.valid_to)}</td>
                      <td className="py-4 px-6">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                                                isCurrent
                                                    ? 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]'
                                                    : 'bg-stone-100 text-stone-600 ring-stone-200'
                                            }`}>
                                                {isCurrent ? 'Current' : 'Ended'}
                                            </span>
                      </td>
                    </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit modal */}
        {showEditForm && product && (
            <ProductForm
                initial={{
                  id:            product.id,
                  sku:           product.sku,
                  name:          product.name,
                  store_id:      product.store_id ?? null,
                  current_price: product.display_price ?? 0,
                  stock:         product.stock ?? 0,
                  description:   product.description ?? '',
                  specification: product.specification ?? '',
                  keywords:      product.keywords ?? [],
                  is_active:     product.is_active ?? true,
                }}
                onClose={() => setShowEditForm(false)}
                onSaved={() => {
                  setShowEditForm(false);
                  if (productId) void fetchProductDetail(productId, storeId);
                }}
            />
        )}
      </div>
  );
};

export default PriceHistoryPage;